import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ACTButton from "./ACTButton";
import Timer from "../Timer";
import { AppSelectDropdownWhite } from "./AppSelectDropdownWhite";
import AccountSelectInputWhite from "./AccountSelectInputWhite";

// The user comes signed in from the ACT Auth Portal (ssoAuth.js; without a session the page goes to
// the portal's login). App.js then loads the jurisdictions the demo backend allows them
// (/demo-api/apps: their SSO jurisdictions that are enabled there). The user types the account id.
const HomePanel = (props) => {
  const { onGenerateQR, sessionData, deeplink, sso, jurisdictions, loadingJurisdictions, jurisdictionsError } = props;
  const timerRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [updateQrCode, setUpdateQrCode] = useState(false);
  const signedIn = sso.status === "signedIn";

  var expTime = sessionData?.expiryTime;

  const handleQrCodeScanned = () => {
    if (timerRef.current) {
      timerRef.current.stopTimer(); // Stop the timer
    }
  };
  const handleTimerExpire = () => {
    setUpdateQrCode(true);
  };

  const onRefreshQrCode = () => {
    onGenerateQR();
    setUpdateQrCode(false);
  };

  const openInVerifyApp = () => {
    window.location.href = deeplink;
  };

  // The spinner shows until the session's first QR has loaded. v1.4 replaces the QR every ~20 s:
  // the next one swaps in place (a data URL can load before an effect runs, so turning the spinner
  // on per QR could leave it on).
  const [qrLoaded, setQrLoaded] = useState(false);
  useEffect(() => {
    if (!sessionData?.qrCode) setQrLoaded(false);
  }, [sessionData?.qrCode]);
  const loading = Boolean(sessionData?.qrCode) && !qrLoaded;

  return (
    <>
      <Box
        sx={{
          marginTop: "4.5rem",
          width: "100%",
        }}
      >

        {!updateQrCode && (
          <>
            <Box
              sx={{
                backgroundColor: "#000000",
                height: "3.5rem",
                width: isMobile ? "90vw" : "26rem",
                padding: "0.5rem 0.75rem",
                borderRadius: "0.5rem",
                gap: "0.5rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "absolute",
                top: "0",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              {/* Jurisdiction dropdown: the user's own (their SSO jurisdictions enabled in the backend) */}
              <AppSelectDropdownWhite apps={jurisdictions} disabled={!signedIn || loadingJurisdictions} />

              <AccountSelectInputWhite />
            </Box>
            {!signedIn ? (
              sso.status === "signedOut" ? (
                // Only when the page can't go to the portal (SSO is unavailable)
                <Box sx={{ width: "85%", margin: "0 auto", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  <Typography data-testid="sso-error" sx={{ fontSize: "1rem", color: "#b00020" }}>
                    {sso.message}
                  </Typography>
                  <ACTButton
                    content="Try again"
                    onClick={() => window.location.reload()}
                    bgColor="#E54F9C"
                    textColor="#ffffff"
                    hoverColor="#b33b78"
                    hoverTextColor="#ffffff"
                    border="0.1rem solid #E54F9C"
                    hoverBorder="0.1rem solid #b33b78"
                  />
                </Box>
              ) : (
                // Picking up the portal's session, or on the way to its login
                <CircularProgress size="1.5rem" sx={{ color: "#E54F9C" }} />
              )
            ) : !sessionData?.qrCode ? (
              <Typography
                sx={{
                  maxWidth: "95%",
                  fontSize: "1.1rem",
                  fontWeight: "500",
                  lineHeight: "1.5",
                }}
              >
                {loadingJurisdictions ? (
                  <CircularProgress size="1.5rem" sx={{ color: "#E54F9C" }} />
                ) : jurisdictionsError ? (
                  <span data-testid="demo-apps-error" style={{ color: "#b00020" }}>
                    Couldn't load your locations: {jurisdictionsError}
                  </span>
                ) : jurisdictions.length === 0 ? (
                  <>No locations are enabled for your account.</>
                ) : (
                  <>
                    Select a <span style={{ color: "#E54F9C" }}>location</span> and enter an{" "}
                    <span style={{ color: "#E54F9C" }}>account ID</span> to continue
                  </>
                )}
              </Typography>
            ) : (
              <>
                {loading && (
                  <Box
                    sx={{
                      height: isMobile ? "125px" : "155px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      position: "absolute",
                      top: "9rem",
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                  >
                    <CircularProgress
                      size="6rem"
                      sx={{
                        color: "#E54F9C",
                      }}
                    />
                  </Box>
                )}
                <img
                  src={sessionData?.qrCode}
                  alt="QR Code"
                  onLoad={() => setQrLoaded(true)}
                  style={{
                    display: loading ? "none" : "flex",
                    width: isMobile ? "125px" : "155px",
                    height: isMobile ? "125px" : "155px",
                    margin: "0 auto",
                    position: "absolute",
                    top: "9rem",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                />
                <Typography
                  sx={{
                    maxWidth: "95%",
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    lineHeight: "1.25",
                    margin: isMobile
                      ? "0.25rem auto 1rem auto"
                      : "0.25rem auto 0 auto",
                    width: "100%",
                    position: "absolute",
                    top: isMobile ? "21rem" : "19.25rem",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  Scan the QR code using the ACTVerify app.
                </Typography>

                {isMobile && (
                  <Box
                    sx={{
                      width: "17rem",
                      margin: isMobile
                        ? "0.75rem auto 0 auto"
                        : "1.5rem auto 0 auto",
                      position: "absolute",
                      top: "16.75rem",
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                  >
                    <ACTButton
                      content="Open in ACTVerify App"
                      onClick={openInVerifyApp}
                      bgColor="#E54F9C"
                      textColor="#ffffff"
                      hoverColor="#b33b78"
                      hoverTextColor="#ffffff"
                      border="0.1rem solid #E54F9C"
                      hoverBorder="0.1rem solid #b33b78"
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
      <Box>
        {sessionData?.expiryTime && (
          <Timer
            style={{
              maxWidth: "12.5rem",
              fontSize: "0.9rem",
              fontWeight: "500",
              margin: isMobile ? "0 auto" : "0.25rem auto",
            }}
            ref={timerRef}
            expiryTime={expTime}
            onExpire={handleTimerExpire}
          />
        )}

        {updateQrCode && (
          <Box
            sx={{
              width: "16rem",
              margin: "0 auto",
              position: "absolute",
              top: "10rem",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <ACTButton
              content="Refresh QR Code"
              onClick={onRefreshQrCode}
              bgColor="#E54F9C"
              textColor="#ffffff"
              hoverColor="#b33b78"
              hoverTextColor="#ffffff"
              border="0.1rem solid #E54F9C"
              hoverBorder="0.1rem solid #b33b78"
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default HomePanel;
